import { CarDetails, EntrepriseDetails } from '@policyvision/contracts';
import { forwardRef } from 'react';

import { ValidableSection } from '@/policy/component/Validation/ValidableSection';
import { useScroll } from '@/policy/hooks/useScroll';
import { Car } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
import { CarListTable } from './CarListTable';

interface CarListSectionProps {
  cars: Car[];
  title: string;
  detail?: CarDetails | EntrepriseDetails;
  carListIdentifier: string;
  validation: ValidationResult;
  onValidationChange: (validation: ValidationResult) => void;
  isSelected: boolean;
  onTableClick: (sectionUuid: string) => void;
}

export const CarListSection = forwardRef<HTMLDivElement, CarListSectionProps>(
  (
    {
      cars,
      title,
      detail,
      carListIdentifier,
      validation,
      onValidationChange,
      isSelected,
      onTableClick,
    },
    ref,
  ) => {
    const subheader = detail && (
      <div className="text-sm text-muted-foreground text-center space-y-1">
        {detail.address && <p>{detail.address}</p>}
        {detail.affectation && <p>{detail.affectation}</p>}
      </div>
    );

    const { scrollContainerRef, dragProps, canScrollLeft, canScrollRight } =
      useScroll();

    return (
      <ValidableSection
        ref={ref}
        title={title}
        subheader={subheader}
        sectionIdentifier={carListIdentifier}
        validation={validation}
        onValidationChange={onValidationChange}
        isSelected={isSelected}
        onTableClick={onTableClick}
      >
        <ScrollShadows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        >
          <div
            ref={scrollContainerRef}
            {...dragProps}
            className={`w-full overflow-x-auto scrollbar-visible bg-white select-none ${dragProps.className}
            [&::-webkit-scrollbar]:h-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
            [&::-webkit-scrollbar-thumb]:rounded-full`}
          >
            <CarListTable cars={cars} />
          </div>
        </ScrollShadows>
      </ValidableSection>
    );
  },
);

CarListSection.displayName = 'CarListSection';
